import 'isomorphic-fetch';
import get from 'lodash/get';
import { API_TIMEOUT } from '../common/constants';
import ApiTrackingError from './ApiTrackingError';
import isIpPrivate from '../../utils/ipAddress';
import { isServerSide } from '../../utils/isomorphic';
import metricsApi from '../metrics/Api';

export const END_POINTS = {
  PAGE_VIEWS: '/tracking/page/views',
  IMPRESSIONS: '/tracking/listing/impressions',
  AD_IMPRESSIONS: '/tracking/listing/impressions',
  CLICK_IN: '/tracking/listing/clicks',
  SAVE_APPLY: '/tracking/source/leads',
  LEAD_FORM_IMPRESSIONS: '/tracking/source/lead-impressions',
  NOTIFICATION_BROWSER_PERMISSIONS: '/tracking/notification/browser/permissions',
  NOTIFICATION_BROWSER_STATUSES: '/tracking/notification/browser/statuses',
  NOTIFICATION_BROWSER_EVENTS: '/tracking/notification/browser/events',
  NOTIFICATION_SAVE_ALERT: '/tracking/notification/email/events',
  ADSENSE_CLICK: '/tracking/source/adsense',
};

const FETCH_OPTIONS = {
  method: 'POST',
  mode: 'cors',
  timeout: API_TIMEOUT,
  headers: {
    Accept: 'application/vnd.api+json',
    'Content-Type': 'application/vnd.api+json',
  },
};

// Checks if body sent by Google Tag Manager is
// falsy, empty object or empty array
function isBodyFalsyOrEmpty(body) {
  if (!body) {
    return true;
  }
  if (typeof body === 'object' && Object.keys(body).length === 0) {
    return true;
  }
  if (Array.isArray(body) && body.length === 0) {
    return true;
  }

  return false;
}

class Api {
  static post(url, body, metricsData) {
    const userIp = get(body, 'data.attributes.user.ip');
    if (isIpPrivate(userIp)) {
      /* TODO: the correct way is return ALWAYS a promise, because the client that consume that method always expect a behaviour async
      (search by zalgo in google): https://medium.com/@bluepnume/intentionally-unleashing-zalgo-with-promises-ab3f63ead2fd */
      return Promise.resolve();
    }

    if (isBodyFalsyOrEmpty(body)) {
      return Promise.resolve('Promise resolved but tracking not created. Body must be defined.');
    }

    const bodySerialized = JSON.stringify(body);
    let response = null;

    const mustSaveMetrics = isServerSide() && typeof metricsData !== 'undefined';
    const startTime = new Date();

    return fetch(`${url}`, { body: bodySerialized, ...FETCH_OPTIONS })
      .then(res => {
        response = res;
        return response.json();
      })
      .then(json => {
        if (json.errors) {
          const error = Array.isArray(json.errors) ? json.errors[0] : json.errors;
          const errorMessage = `${json.reason} - ${error.error}`;

          if (mustSaveMetrics) {
            metricsApi.incrementError(metricsData).catch(errorApi => {
              throw errorApi;
            });
          }

          throw new ApiTrackingError(error.status, errorMessage, response.status);
        }
        if (mustSaveMetrics) {
          const endTime = new Date();
          metricsApi.incrementQuery(metricsData).catch(errorApi => {
            throw errorApi;
          });

          const responseTime = endTime - startTime;
          metricsApi.storeResponseTime({ ...metricsData, responseTime }).catch(errorApi => {
            throw errorApi;
          });
        }

        return json;
      })
      .catch(error => {
        if (error.type === 'request-timeout') {
          if (mustSaveMetrics) {
            metricsApi.incrementTimeout(metricsData).catch(errorApi => {
              throw errorApi;
            });
          }
        }
        // this catch does not report errors to Sentry manually
        // because there is still unhandledRejections to solve
        // and will cause duplicated logs in Sentry
        // @see ../search/Api.js as an example to include this function
        // reportErrorToSentry(error);
        throw error;
      });
  }

  constructor(urlBase) {
    this.urlBase = urlBase;
  }

  /**
   * Request clickout to know the Ad's target
   * @param {Object} data
   * @param {Object} metricsData
   * @returns {Object}
   *
   * @throws {ApiTrackingError}
   * @memberof Api
   */
  pageview(data, metricsData) {
    const url = `${this.urlBase}${END_POINTS.PAGE_VIEWS}`;
    const body = data;

    return Api.post(url, body, metricsData);
  }

  /**
   * Send sections impressions info to api tracking
   * TODO: move adImpressions() to here
   * @param {Object} data
   * @returns {Object}
   *
   * @throws {ApiTrackingError}
   * @memberof Api
   */
  impression(data) {
    const url = `${this.urlBase}${END_POINTS.IMPRESSIONS}`;
    const body = data;

    return Api.post(url, body);
  }

  /**
   * Send ads sections impressions info to api tracking
   * @param {Object} data
   * @param {Object} metricsData
   * @returns {Object}
   *
   * @throws {ApiTrackingError}
   * @memberof Api
   */
  adImpressions(data, metricsData = {}) {
    const url = `${this.urlBase}${END_POINTS.AD_IMPRESSIONS}`;
    const body = data;

    return Api.post(url, body, metricsData);
  }

  /**
   * Send lead form impression tracking info to kafka
   * @param {Object} data
   * @returns {Object}
   *
   * @throws {ApiTrackingError}
   * @memberof Api
   */
  leadFormImpressions(data) {
    const url = `${this.urlBase}${END_POINTS.LEAD_FORM_IMPRESSIONS}`;
    const body = data;

    return Api.post(url, body);
  }

  /**
   * Send lead tracking info to kafka
   * @param {Object} data
   * @returns {Object}
   *
   * @throws {ApiTrackingError}
   * @memberof Api
   */
  saveApply(data) {
    const url = `${this.urlBase}${END_POINTS.SAVE_APPLY}`;
    const body = data;

    return Api.post(url, body);
  }

  /**
   * Send tracking when user create an alert with a search info to kafka
   * @param {Object} data
   * @returns {Object}
   *
   * @throws {ApiSourcesError}
   * @memberof Api
   */
  saveAlert(data) {
    const url = `${this.urlBase}${END_POINTS.NOTIFICATION_SAVE_ALERT}`;
    const body = data;

    return Api.post(url, body);
  }

  /**
   * Send notification browser permissions info to kafka
   * @param {Object} data
   * @returns {Object}
   *
   * @throws {ApiTrackingError}
   * @memberof Api
   */
  notificationBrowserPermissions(data) {
    const url = `${this.urlBase}${END_POINTS.NOTIFICATION_BROWSER_PERMISSIONS}`;
    const body = data;

    return Api.post(url, body);
  }

  /**
   * Send notification browser searches status info to kafka
   * @param {Object} data
   * @returns {Object}
   *
   * @throws {ApiSourcesError}
   * @memberof Api
   */
  notificationBrowserStatuses(data) {
    const url = `${this.urlBase}${END_POINTS.NOTIFICATION_BROWSER_STATUSES}`;
    const body = data;

    return Api.post(url, body);
  }

  /**
   * Send browser event related con push notifications
   * @param {Object} data
   * @returns {Object}
   *
   * @throws {ApiTrackingError}
   * @memberof Api
   */
  notificationBrowserEvents(data) {
    const url = `${this.urlBase}${END_POINTS.NOTIFICATION_BROWSER_EVENTS}`;
    const body = data;

    return Api.post(url, body);
  }

  /**
   * Send an event to api tracking
   * @param {String} endpoint
   * @param {Object} data
   * @returns {void}
   *
   * @memberof Api
   */
  sendTracking(endpoint, data) {
    const url = `${this.urlBase}${endpoint}`;

    return Api.post(url, data);
  }

  /**
   * Send tracked click
   * @param {*} data
   * @returns {Object}
   *
   * @throws {ApiTrackingError}
   * @memberof Api
   */
  click(data) {
    const url = `${this.urlBase}${END_POINTS.CLICK_IN}`;
    const body = data;

    return Api.post(url, body);
  }
}

export default Api;



// WEBPACK FOOTER //
// ./app/sdk/tracking/Api.js